import { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, Pencil, Trash2, KeyRound, ShieldCheck, Ban, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { ROLES } from "../lib/format";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const ROLE_OPTS = [
  { value: "admin", label: "Super Administrator" },
  { value: "director", label: "Director" },
  { value: "bdm", label: "Business Development Manager" },
  { value: "trainer", label: "Clinical Trainer" },
  { value: "finance", label: "Finance Manager" },
];

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteUser, setDeleteUser] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [includeProducts, setIncludeProducts] = useState(false);
  const [includeStaff, setIncludeStaff] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const runReset = async () => {
    setResetBusy(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: resetConfirm, include_products: includeProducts, include_sample_staff: includeStaff });
      const total = Object.values(data.cleared).reduce((a, b) => a + b, 0);
      toast.success(`Cleared ${total} records. Your CRM is now a clean slate.`);
      setResetOpen(false); setResetConfirm(""); setIncludeProducts(false); setIncludeStaff(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetBusy(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm({ role: "bdm", name: "", email: "", title: "", password: "" }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, role: u.role, title: u.title || "", password: "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, title: form.title };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.email || !form.name || !form.password) { toast.error("Name, email and password are required"); return; }
        await api.post("/users", form);
        toast.success("User created — share their email & password to sign in");
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "User deactivated" : "User reactivated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    try { await api.delete(`/users/${deleteUser.id}`); toast.success("User deleted"); setDeleteUser(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); setDeleteUser(null); }
  };

  return (
    <div>
      <PageHeader title="User Management" subtitle="Create and manage staff accounts & access" icon={UsersIcon}>
        <Button onClick={openCreate} className="bg-navy-900 hover:bg-navy-800" size="sm" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add User
        </Button>
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 bg-slate-50/60">
            {["Name", "Email", "Role", "Status", "Actions"].map((h) => (
              <th key={h} className={`text-${h === "Actions" ? "right" : "left"} text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-4 py-3 whitespace-nowrap`}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/70" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-navy-900">{u.name}</span>
                    {u.email === me?.email && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">You</span>}
                  </div>
                  {u.title && <div className="text-xs text-slate-400">{u.title}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${(ROLES[u.role] || {}).color || "bg-slate-200"}`}>
                    {(ROLES[u.role] || {}).label || u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${u.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                    {u.active ? <CheckCircle2 className="h-3 w-3" /> : <Ban className="h-3 w-3" />}
                    {u.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => openEdit(u)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Edit / reset password" data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => toggleActive(u)} className="p-1.5 rounded-md hover:bg-amber-50 text-amber-600" title={u.active ? "Deactivate" : "Reactivate"} data-testid={`toggle-user-${u.id}`}>
                      {u.active ? <Ban className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setDeleteUser(u)} className="p-1.5 rounded-md hover:bg-red-50 text-red-500" title="Delete" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 bg-white rounded-xl border border-red-200 shadow-sm p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="h-11 w-11 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-semibold text-navy-900">Danger Zone — Clear Business Data</h3>
            <p className="text-sm text-slate-500 mt-0.5">Permanently delete all clinics, contacts, opportunities, meetings, mileage, expenses, devices, workshops, tasks, documents & commissions. User accounts are kept. Use this to start with a clean slate.</p>
          </div>
          <Button variant="outline" onClick={() => setResetOpen(true)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="open-reset-btn">
            Clear business data
          </Button>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Clear all business data?</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">This <strong>permanently deletes</strong> all clinics, contacts, opportunities, meetings, mileage, expenses, devices, workshops, tasks, documents and commissions. This cannot be undone.</p>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox checked={includeProducts} onCheckedChange={setIncludeProducts} data-testid="include-products" />
              Also delete the product catalogue
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <Checkbox checked={includeStaff} onCheckedChange={setIncludeStaff} data-testid="include-staff" />
              Also remove the 5 sample staff accounts (keeps you & any users you added)
            </label>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Type <span className="font-mono text-red-600">DELETE</span> to confirm</label>
              <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="DELETE" data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button onClick={runReset} disabled={resetConfirm !== "DELETE" || resetBusy} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reset-btn">
              {resetBusy ? "Clearing…" : "Clear data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-brand" /> {editing ? "Edit User" : "Add New User"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Full Name *</label>
              <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
            {!editing && (
              <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email *</label>
                <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@dermalis.co.nz" data-testid="user-email" /></div>
            )}
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Job Title</label>
              <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="user-title" /></div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">Role *</label>
              <Select value={form.role || ""} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{ROLE_OPTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-semibold text-slate-600 mb-1.5 block">{editing ? "Reset Password (optional)" : "Temporary Password *"}</label>
              <Input type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" data-testid="user-password" />
              <p className="text-[11px] text-slate-400 mt-1">The user can change this after first login.</p></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-navy-900 hover:bg-navy-800" data-testid="save-user-btn">{editing ? "Save changes" : "Create user"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteUser} onOpenChange={(o) => !o && setDeleteUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteUser?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes their account and access. Consider deactivating instead if this is temporary.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
